import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '../utils/apiClient';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

// Create the context
const AppContext = createContext();

// Hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// AppProvider component
export const AppProvider = ({ children }) => {
  // URL parameters - App Bridge 4.x handles this automatically
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get('shop');

  // Get authenticated API for App Bridge React 4.x
  console.log('🔍 AppContext: About to call useAuthenticatedApi with shop:', shop);
  let authenticatedRequest;
  let authenticatedFormDataRequest;
  try {
    const apiHook = useAuthenticatedApi(shop);
    authenticatedRequest = apiHook.authenticatedRequest;
    authenticatedFormDataRequest = apiHook.authenticatedFormDataRequest;
    console.log('✅ AppContext: useAuthenticatedApi succeeded');
  } catch (error) {
    console.error('❌ AppContext: useAuthenticatedApi failed:', error);
    // Fallback to regular API
    authenticatedRequest = async (method, endpoint, data, params) => {
      console.warn('⚠️ Using fallback API without authentication');
      return api.request(method, endpoint, data, params);
    };
    authenticatedFormDataRequest = async (method, endpoint, formData) => {
      console.warn('⚠️ Using fallback FormData API without authentication');
      throw new Error('Authenticated FormData upload not available');
    };
  }

  // Authentication states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Initialization states
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [inclubData, setInclubData] = useState(null);
  // Approved-store allowlist flag from /init (not_connected only). Decides
  // whether the landing shows "Connect to InClub" or "Contact us".
  const [storeApproved, setStoreApproved] = useState(false);
  const [members, setMembers] = useState([]);
  const [invitedMembers, setInvitedMembers] = useState([]);

  // Modal states for connection issues
  const [showReconnectModal, setShowReconnectModal] = useState(false);

  // Global error modal state
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState('Error');

  // Initialize app data and check connection status
  const initializeAppData = useCallback(async (shopDomain) => {
    setIsInitializing(true);
    setInitError(null);

    try {
      console.log('🔄 Initializing app for shop:', shopDomain);

      // Use authenticated API - shop domain comes from verified session token
      const response = await authenticatedRequest('GET', '/init');
      console.log('✅ Initialization response:', response);

      setConnectionStatus(response.status);
      setStoreApproved(response.approved === true);
      setStoreData(response.store || null);
      setInclubData(response.inclub || null);
      setMembers(response.members || []);
      setInvitedMembers(response.invitedMembers || []);

      // Handle different connection states
      switch (response.status) {
        case 'no_inclub':
          console.log('📝 No InClub found - user can connect via Dashboard button');
          break;
        case 'store_disconnected':
          setShowReconnectModal(true);
          break;
        case 'connected':
          console.log('✅ App fully initialized and connected');
          break;
        default:
          console.warn('⚠️ Unknown initialization status:', response.status);
      }

    } catch (error) {
      console.error('❌ App initialization failed:', error);
      setInitError(error.message || 'Failed to initialize app');
    } finally {
      setIsInitializing(false);
    }
  }, [authenticatedRequest]);

  // Global error handler
  const showErrorModal = useCallback((message, title = 'Error') => {
    setErrorModalTitle(title);
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  }, []);

  const closeErrorModal = useCallback(() => {
    setErrorModalOpen(false);
    setErrorModalMessage('');
    setErrorModalTitle('Error');
  }, []);

  // API Methods for updating state after successful responses
  const updateInclubData = useCallback((updatedInclub) => {
    setInclubData(prevData => ({
      ...prevData,
      ...updatedInclub
    }));
  }, []);

  const apiMethods = {
    // Note: getInclubSettings removed - settings data now comes from /init in inclubData

    // Update InClub details and refresh context state
    updateInclubDetails: async (formData) => {
      if (!inclubData?._id) {
        throw new Error('InClub ID not available');
      }
      const response = await authenticatedFormDataRequest('PUT', `/inclub/${inclubData._id}/details`, formData);
      if (response.success) {
        updateInclubData(response.inclub);
      }
      return response;
    },

    // Unified settings update - combines branding and tiers in one transaction
    // (the points half went with the gamification layer on 2026-09-07)
    updateAllSettings: async (settingsData) => {
      if (!inclubData?._id) {
        throw new Error('InClub ID not available');
      }

      const { branding, tiers, tierFiles, tierFileChanges } = settingsData;

      // Check if we have any files to upload (branding images or tier files)
      const hasFiles = (branding?.headerImageFile || branding?.avatarImageFile || (tierFiles && tierFiles.length > 0));

      if (hasFiles) {
        // Build FormData for multipart upload
        const formData = new FormData();

        // Add branding data
        if (branding) {
          formData.append('branding', JSON.stringify({
            name: branding.name,
            description: branding.description
          }));

          // Add branding image files
          if (branding.headerImageFile) {
            formData.append('headerImage', branding.headerImageFile);
          }
          if (branding.avatarImageFile) {
            formData.append('avatarImage', branding.avatarImageFile);
          }
        }

        // Add tiers data
        if (tiers) {
          formData.append('tiers', JSON.stringify(tiers));
        }

        // Add tier file changes
        if (tierFileChanges) {
          formData.append('fileChanges', JSON.stringify(tierFileChanges));
        }

        // Add tier files
        if (tierFiles && tierFiles.length > 0) {
          tierFiles.forEach((file) => {
            formData.append(`tier_${file.tierId}_files`, file.file, file.name);
          });
        }

        const response = await authenticatedFormDataRequest('PUT', `/inclub/${inclubData._id}/settings`, formData);
        if (response.success) {
          updateInclubData(response.inclub);
        }
        return response;
      } else {
        // Send as regular JSON when no files
        const payload = {};

        // Clean branding data - remove file properties for JSON requests
        if (branding) {
          const { headerImageFile, avatarImageFile, ...brandingData } = branding;
          payload.branding = brandingData;
        }

        if (tiers) payload.tiers = tiers;

        console.log('📤 Sending JSON payload:', JSON.stringify(payload, null, 2));

        const response = await authenticatedRequest('PUT', `/inclub/${inclubData._id}/settings`, payload);
        if (response.success) {
          updateInclubData(response.inclub);
        }
        return response;
      }
    },

    // Update InClub tiers and refresh context state
    updateInclubTiers: async (tiersData, fileChanges, files) => {
      if (!inclubData?._id) {
        throw new Error('InClub ID not available');
      }

      // Check if we have files to upload
      const hasFiles = files && files.length > 0;

      if (hasFiles) {
        // Build FormData for tier upload with files
        const formData = new FormData();
        formData.append('tiers', JSON.stringify(tiersData));

        if (fileChanges) {
          formData.append('fileChanges', JSON.stringify(fileChanges));
        }

        files.forEach((file) => {
          formData.append(`tier_${file.tierId}_files`, file.file, file.name);
        });

        const response = await authenticatedFormDataRequest('PUT', `/inclub/${inclubData._id}/tiers`, formData);
        if (response.success) {
          updateInclubData(response.inclub);
        }
        return response;
      } else {
        // Send as regular JSON when no files
        const response = await authenticatedRequest('PUT', `/inclub/${inclubData._id}/tiers`, { tiers: tiersData });
        if (response.success) {
          updateInclubData(response.inclub);
        }
        return response;
      }
    }
    // updateInclubPointsSystem removed 2026-09-07 with the gamification layer.
    // It had no callers and its route (PUT /inclub/:id/points-system) is gone.
  };

  // App Bridge React 4.x initialization
  useEffect(() => {
    const initializeApp = async () => {
      if (!shop) {
        setAuthError('Shop parameter is missing. Please access the app through Shopify Admin.');
        return;
      }

      console.log('🔄 Initializing App Bridge React 4.x for shop:', shop);

      try {
        // App Bridge React 4.x handles app creation automatically
        // We just need to set up our API client with shop domain
        // The App Bridge instance will be available via useAppBridge hook in components
        api.setShopifyAuth(shop);
        setIsAuthenticated(true);
        console.log('✅ App Bridge React 4.x authentication successful');

        // Initialize our app data
        await initializeAppData(shop);
      } catch (error) {
        console.error('❌ App initialization failed:', error);
        setAuthError(`Failed to initialize app: ${error.message}`);
      }
    };

    if (shop) {
      initializeApp();
    }
  }, [shop]);


  // Context value
  const contextValue = {
    // Authentication state
    isAuthenticated,
    authError,

    // Initialization state
    isInitializing,
    initError,

    // Connection state
    connectionStatus,
    storeApproved,
    storeData,
    inclubData,
    members,
    invitedMembers,

    // Modal states
    showReconnectModal,
    setShowReconnectModal,
    errorModalOpen,
    errorModalMessage,
    errorModalTitle,

    // Shop info
    shop,

    // Actions
    initializeApp: initializeAppData,
    updateContext: () => initializeAppData(shop),

    // Error handling
    showErrorModal,
    closeErrorModal,

    // Authenticated API request
    authenticatedRequest,

    // API methods that update context state
    ...apiMethods,

    // Helper methods
    isFullyConnected: connectionStatus === 'connected' && storeData && inclubData,
    hasStoreConnection: isAuthenticated && storeData,
    hasInClubConnection: !!inclubData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;